import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import JobsiteApi from '../jobsite/jobsiteApi';
import TimecardApi from './timecardApi';
import { Timecard, TimecardDurationReport } from './timecardType';
import { Jobsite } from '../jobsite/jobsiteType';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import TimecardDisplay from './timecardDisplay';
import InOutButtonDisplay from './InOutButtonDisplay';
import { getCurrentTimestamp, getCurrentDate } from '../lib/global_function';
import EmployeeApi from '../employee/employeeApi';


import { Alert } from '../components/ui/alert';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';

const TimecardPage: React.FC = () => {
  const [mapGreyedOut, setMapGreyedOut] = useState(true);
  const { employee_code } = useParams<{ employee_code: string }>();

  const [selectedJobsiteName, setSelectedJobsiteName] = useState<string>('');
  const [companyCode, setCompanyCode] = useState<string>('');
  const [employeeFullName, setEmployeeFullName] = useState<string>('');
  const [selectedJobsiteId, setSelectedJobsiteId] = useState<string>('');
  const [jobsites, setJobsites] = useState<Jobsite[]>([]);
  const [timecards, setTimecards] = useState<Timecard[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>('');
  const [message, setMessage] = useState<string | null>('');
  const [mapCenter, setMapCenter] = useState({ lat: 28.0517, lng: -82.4588 });
  const [fullAddress, setFullAddress] = useState<string>('');
  const [displaySessions, setDisplaySessions] = useState<Timecard[]>([]);
  const [timecardDurationReport, setTimecardDurationReport] = useState<TimecardDurationReport | null>(null);

  useEffect(() => {
    if (timecards) {
      setDisplaySessions(timecards);
    } else {
      setDisplaySessions([]);
    }
  }, [timecards]);

  useEffect(() => {
    setErrorMessage('');
    loadJobsites();
    getTimeCardShortReport();
    getEmployNameByCode();
  }, []);

  const loadJobsites = async () => {
    try {
      const jobsiteData = await JobsiteApi.getJobsiteByEmployeeCode(employee_code as string);
      setJobsites(jobsiteData);
      //console.log("response=====:", jobsiteData);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const getEmployNameByCode = async () => {
    try {
      const response = await EmployeeApi.getEmployeeByCode(employee_code as string);
      const fullName = response.data.attributes.first_name + ' ' + response.data.attributes.last_name;
      setEmployeeFullName(fullName);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const getTimeCardShortReport = async () => {
    try {
      const response: TimecardDurationReport = await TimecardApi.getTimeCardShortReport(
        employee_code as string,
        true,
        true,
        true
      );
      setTimecardDurationReport(response);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  useEffect(() => {
    setErrorMessage('');
    if (selectedJobsiteName) {
      getEmployeeTimecards(getCurrentDate());
    }
  }, [selectedJobsiteName]);

  const getEmployeeTimecards = async (currentDate: string) => {
    try {
      const response = await TimecardApi.getEmployeeTimecards(
        employee_code as string,
        selectedJobsiteId as string,
        currentDate as string
      );

      if (response.status === 200) {
        const timecardData = response.responseBody.data.map((item: { attributes: Timecard }) => item.attributes);
        setTimecards(timecardData);
      } else {
        setTimecards(null);
      }
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const handleClockIn = async () => {
    setErrorMessage('');
    setMessage('');

    try {
      if (!selectedJobsiteId || !employee_code) {
        setErrorMessage('Please select a jobsite.');
        return;
      }

      const response = await TimecardApi.createTimecard(
        companyCode as string,
        employee_code as string,
        selectedJobsiteId as string
      );

      if (response.status === 201) {
        const timecardAttributes = response.responseBody.data.attributes;
        setTimecards(timecardAttributes);
        await getEmployeeTimecards(getCurrentDate());
      } else if (response.status === 422) {
        setErrorMessage('An active time card already exists for this employee: ' + response.status);
      } else {
        setErrorMessage('Clock in failed. Please try again. status: ' + response.status);
      }
    } catch (error) {
      setErrorMessage('An error occurred while create the work session.');
    }
  };

  const handleClockOut = async () => {
    setErrorMessage('');
    setMessage('');

    if (timecards) {
      const sessionToUpdate = timecards.find(session => session.clock_out === null);

      if (sessionToUpdate) {
        const timecardId = sessionToUpdate.timecard_id;
        const currentTimestamp = getCurrentTimestamp();

        try {
          const response = await TimecardApi.updateTimecard(timecardId as string, currentTimestamp as string);
          getEmployeeTimecards(getCurrentDate());
          getTimeCardShortReport();
          setMessage('Clock out Successfully');
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
          } else {
            setErrorMessage('An unknown error occurred.');
          }
        }
      } else {
        setErrorMessage('No work session found with null clock_out.');
      }
    }
  };

  const handleJobsiteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const jobsiteId = event.target.value;
    setErrorMessage('');
    setMessage('');
    setSelectedJobsiteId(jobsiteId);

    if (jobsiteId) {
      const selectedJobsite = jobsites.find(jobsite => {
        return String(jobsite.jobsite_id) === String(jobsiteId);
      });

      if (selectedJobsite) {
        setCompanyCode(selectedJobsite.company_code);
        const { address_1, city, state, zip, name } = selectedJobsite;
        setSelectedJobsiteName(name);
        setFullAddress(address_1 + ", " + city + ", " + state + " " + zip);
        setMapGreyedOut(false);
        //console.log("selectedJobsite.company_code   == ", selectedJobsite.company_code);
        updateMap(address_1, city, state, zip);
      }
    } else {
      setMapGreyedOut(true);
    }
  };

  const updateMap = useCallback((address: string, city: string, state: string, zip: string) => {
    if (address && city && state && zip) {
      const fullAddress = `${address}, ${city}, ${state} ${zip}`;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: fullAddress }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          setMapCenter({ lat: location.lat(), lng: location.lng() });
        } else {
          setErrorMessage('Geocode was not successful for the following reason: ' + status);
        }
      });
    }
  }, [jobsites]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  return (
    <>

      <div className="container mx-auto max-w-7xl px-4 py-6">

        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''} libraries={['places']}>
          <form onSubmit={handleSubmit}>
            <div className="text-center space-y-4">
              <h2 className="text-2xl font-semibold">
                Time Card <span className="inline">On {getCurrentDate()}</span>
              </h2>

              <div className="space-y-2 text-base">
                <div>
                  <span className="font-semibold">Employee Name: </span>
                  <span>{employeeFullName}</span>
                  <span className="ml-4 font-semibold">ID:</span>
                  <span className="ml-2">{employee_code}</span>
                </div>
              </div>

              {message && <Alert variant="success">{message}</Alert>}
              {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

              {timecardDurationReport && (
                <div className="space-y-2 text-base">
                  <div>
                    <span className="font-semibold">Hours Worked Today: </span>
                    <span>{timecardDurationReport.current_day?.duration || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Hours Worked This Week: </span>
                    <span>{timecardDurationReport.current_week?.duration || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Hours Worked This Month: </span>
                    <span>{timecardDurationReport.current_month?.duration || 'N/A'}</span>
                  </div>
                </div>
              )}

              <TimecardDisplay timecards={timecards} />

              {jobsites.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex flex-col items-center gap-2">
                    <Label htmlFor="jobsite" className="font-semibold">
                      Select Jobsite:
                    </Label>
                    <Select
                      id="jobsite"
                      value={selectedJobsiteId}
                      onChange={handleJobsiteChange}
                      required
                      className="w-full max-w-md"
                    >
                      <option value="">Select a jobsite</option>
                      {jobsites.map((jobsite) => (
                        <option key={jobsite.jobsite_id} value={jobsite.jobsite_id}>
                          {jobsite.name}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <InOutButtonDisplay
                    timecards={timecards}
                    onClockIn={handleClockIn}
                    onClockOut={handleClockOut}
                  />

                  <div className="flex justify-center">
                    <Input
                      id="address"
                      name="address"
                      value={fullAddress}
                      readOnly
                      className="w-full max-w-md border-none text-center"
                    />
                  </div>

                  <div className="relative h-96 w-full max-w-4xl mx-auto">
                    <GoogleMap
                      center={mapCenter}
                      zoom={11}
                      mapContainerStyle={{ height: '100%', width: '100%' }}
                    >
                      <Marker position={mapCenter} />
                    </GoogleMap>
                    {mapGreyedOut && (
                      <div className="absolute inset-0 bg-gray-300/70 flex justify-center items-center z-10">
                        <p className="text-gray-700 text-lg font-medium">Select a jobsite to view the map</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  {employee_code ? (
                    <p className="text-gray-600">No Jobsite Available</p>
                  ) : (
                    <p className="text-red-600">Unhandled Exceptions</p>
                  )}
                </div>
              )}
            </div>
          </form>
        </LoadScript>
      </div>
    </>
  );
};

export default TimecardPage;